import { apiFetch, apiPost, zeigeApiFehler } from '../../lib/api';
import { toast } from '../../lib/toast';

// Welle U4b: gemeinsame Basis für Profil-Karte, Notizen-Screen und Bearbeiten-Sheet.
// Datenquelle ist ausschließlich GET /api/notizen (U4a) — kein Sitzungsspeicher mehr.

export interface Notiz {
  id: number; schueler_id: number; lehrer_id: number;
  fach: string | null; datum: string; kategorie: string;
  aktion: string | null; zusammenfassung: string | null;
  valenz: 'positiv' | 'negativ' | null;
  angepinnt: 0 | 1; archiviert: boolean; archiviert_am: string | null;
  quelle: 'manuell' | 'diktat' | 'schnelldiktat' | null;
  created_at: string | null; updated_at: string | null;
  schuljahr: string | null;
  schueler_name: string; vorname: string; nachname: string; klasse_name: string | null;
}
export interface NotizenAntwort { notizen: Notiz[]; gesamt: number; limit: number; offset: number; kategorien: Record<string, number> }

// Kategorien: Hintergrundfarben aus dem bestehenden farbeAusKategorie (App.tsx) — eine Quelle.
// Negativ bleibt Amber-Tint, nie Rot.
// Welle K2: punkt = Farbe des kleinen Kategorie-Punkts in der Notizen-Liste (keine Flächen mehr)
export const NOTIZ_KATEGORIEN: Record<string, { label: string; icon: string; bg: string; ink: string; punkt?: string }> = {
  Positiv:      { label: 'Positiv',     icon: 'thumb_up',        bg: '#D1FAE5', ink: '#166534', punkt: 'var(--ld-success)' },
  Negativ:      { label: 'Negativ',     icon: 'thumb_down',      bg: '#FEF3C7', ink: '#92400E', punkt: 'var(--ld-warning)' },
  HA_vergessen: { label: 'HA vergessen', icon: 'assignment_late', bg: '#FDE8D0', ink: '#9A3412', punkt: '#C2410C' },
  Sonstiges:    { label: 'Sonstiges',   icon: 'sticky_note_2',   bg: 'var(--ld-bg-input)', ink: 'var(--ld-text-secondary)' },
  Fehlzeit:     { label: 'Fehlzeit',    icon: 'event_busy',      bg: '#FEE2E2', ink: '#9F1239', punkt: 'var(--ld-danger)' },
};
export const WAEHLBARE_KATEGORIEN = ['Positiv', 'Negativ', 'HA_vergessen', 'Sonstiges'];
export const katInfo = (k: string) => NOTIZ_KATEGORIEN[k] ?? { label: k.replace(/_/g, ' '), icon: 'sticky_note_2', bg: 'var(--ld-bg-input)', ink: 'var(--ld-text-secondary)' };
export const valenzAus = (k: string): 'positiv' | 'negativ' | null => (k === 'Positiv' ? 'positiv' : k === 'Negativ' ? 'negativ' : null);

export const QUELLE: Record<string, { text: string; icon: string }> = {
  diktat: { text: 'Per Diktat', icon: 'mic' },
  schnelldiktat: { text: 'Schnell-Diktat', icon: 'bolt' },
  manuell: { text: 'Von dir notiert', icon: 'edit' },
};
export const quelleInfo = (q: string | null) => (q && QUELLE[q]) || { text: 'ohne Angabe', icon: 'help' };

export const notizText = (n: Pick<Notiz, 'zusammenfassung' | 'aktion'>) => n.zusammenfassung ?? n.aktion ?? '';

const WT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
export const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const d = (iso: string) => new Date(iso.slice(0, 10) + 'T12:00:00');
const z2 = (n: number) => String(n).padStart(2, '0');
export const ttmm = (iso: string) => { const x = d(iso); return `${z2(x.getDate())}.${z2(x.getMonth() + 1)}.`; };
export const ttmmjjjj = (iso: string) => `${ttmm(iso)}${d(iso).getFullYear()}`;
export const wtTtmm = (iso: string) => `${WT[d(iso).getDay()]} ${ttmm(iso)}`;
// created_at/updated_at sind UTC-ISO → als Ortsdatum anzeigen
export const ttmmUtc = (ts: string) => { const x = new Date(ts); return isNaN(x.getTime()) ? '' : `${z2(x.getDate())}.${z2(x.getMonth() + 1)}.`; };
export const initialen = (name: string) => name.split(/\s+/).filter(Boolean).map(t => t[0]).slice(0, 2).join('').toUpperCase();

// Alle Ansichten (Profil, Screen) laden nach einer Änderung über dieses Ereignis neu.
export const NOTIZEN_RELOAD = 'lehrerdesk-notizen-reload';
export const signalNotizenGeaendert = () => window.dispatchEvent(new Event(NOTIZEN_RELOAD));

export function ladeNotizen(filter: Record<string, string | number | undefined | null>) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  return apiFetch<NotizenAntwort>(`/api/notizen?${qs.toString()}`);
}

export const setzePin = (n: Notiz, angepinnt: boolean) =>
  apiFetch(`/api/notizen/${n.id}`, { method: 'PATCH', body: JSON.stringify({ angepinnt }) });
export const setzeArchiv = (n: Notiz, archiviert: boolean) =>
  apiFetch(`/api/notizen/${n.id}`, { method: 'PATCH', body: JSON.stringify({ archiviert }) });

// Löschen ohne Rückfrage, dafür 5 s "Rückgängig": legt die Notiz mit den Werten aus der
// DELETE-Antwort neu an (neue ID); ein archivierter Stand wird wiederhergestellt.
export async function loescheMitRueckgaengig(n: Notiz) {
  try {
    const r = await apiFetch<{ notiz: any }>(`/api/notizen/${n.id}`, { method: 'DELETE' });
    signalNotizenGeaendert();
    toast('Notiz gelöscht', 'success', {
      dauerMs: 5000,
      aktion: { label: 'Rückgängig', onClick: async () => {
        const g = r.notiz;
        try {
          const neu = await apiPost<{ id: number }>('/api/notizen', {
            schueler_id: g.schueler_id, fach: g.fach, datum: g.datum, kategorie: g.kategorie, aktion: g.aktion,
            zusammenfassung: g.zusammenfassung, valenz: g.valenz, angepinnt: !!g.angepinnt, quelle: g.quelle, created_at: g.created_at,
          });
          if (g.archiviert_am) await apiFetch(`/api/notizen/${neu.id}`, { method: 'PATCH', body: JSON.stringify({ archiviert: true }) });
          signalNotizenGeaendert();
          toast('Notiz wiederhergestellt', 'info');
        } catch (err) { zeigeApiFehler(err, 'Wiederherstellen fehlgeschlagen'); }
      } },
    });
  } catch (err) { zeigeApiFehler(err, 'Löschen fehlgeschlagen'); }
}

// "TT.MM.JJJJ · Name (Klasse) · Fach · Kategorie: Text" — eine Zeile je Notiz, Datum aufsteigend
export function notizenAlsText(liste: Notiz[]) {
  return [...liste]
    .sort((a, b) => a.datum.localeCompare(b.datum) || a.id - b.id)
    .map(n => [
      ttmmjjjj(n.datum),
      `${n.schueler_name}${n.klasse_name ? ` (${n.klasse_name})` : ''}`,
      ...(n.fach ? [n.fach] : []),
      `${katInfo(n.kategorie).label}: ${notizText(n).replace(/\s*\n\s*/g, ' ')}`,
    ].join(' · '))
    .join('\n');
}

async function inZwischenablage(text: string) {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* Rückfall unten */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

export async function kopiereNotizen(liste: Notiz[]) {
  if (liste.length === 0) return;
  const ok = await inZwischenablage(notizenAlsText(liste));
  if (ok) toast(liste.length === 1 ? '1 Notiz kopiert' : `${liste.length} Notizen kopiert`, 'success');
  else toast('Kopieren nicht möglich — bitte Text markieren und manuell kopieren.', 'error');
}
